import { useEffect, useMemo, useRef, FC, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Form as AriaForm, Button } from "react-aria-components";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/react";
import equal from "fast-deep-equal/react";
import { usePostHog } from "posthog-js/react";

// @ts-ignore
import LoaderCircle from "svg/loader-circle.svg";
import Header from "components/Header";
import Field from "components/Field";
import { useSafeState } from "hooks/useSafeState";
import { fetchPublicAPI } from "hooks/usePublicAPI";
import Screen from "./Screen";
import ReviewScreen from "./ReviewScreen";
import { FormConfig } from "./types";
import { buildAnswer, buildFormResponseData } from "./helpers";
import {
  getRelevantFieldRefs,
  getConditionVarValue,
  evaluateCondition,
  resolveEndScreenRef,
} from "./logic";
import {
  FormConfigProvider,
  useFormProgress,
  useFormRedirect,
  useFormStorage,
} from "./hooks";
import ProgressBar from "./ProgressBar";
import DebugPanel from "./DebugPanel";

function flatten(prefix: string, source: Record<string, unknown> = {}) {
  const result: Record<string, unknown> = {};

  Object.entries(source).forEach(([key, value]) => {
    result[`${prefix}.${key}`] = value;
  });

  return result;
}

function useFormVariables({
  formConfig,
  formContext,
  watch,
}: {
  formConfig: FormConfig;
  formContext: Record<string, any>;
  watch: any;
}) {
  const [variables, setVariables] = useSafeState(formConfig.variables || {});
  const formData = watch();

  useEffect(() => {
    // Recompute variables based on current form data
    let updatedVariables = { ...formConfig.variables }; // Start from default variables

    // Iterate over all logic jumps
    if (formConfig.logic) {
      for (const logicJump of formConfig.logic) {
        // Process all 'set' actions
        for (const action of logicJump.actions) {
          if (action.action === "set") {
            const conditionMet = evaluateCondition(
              action.condition,
              () => formData,
              updatedVariables,
              formContext,
            );

            if (conditionMet) {
              const targetVariable = action.details.target.value;
              const value = getConditionVarValue(
                action.details.value,
                () => formData,
                updatedVariables,
                formContext,
              );

              updatedVariables[targetVariable] = value;
            } else {
              // Optionally reset variable to default if condition is not met
              // updatedVariables[targetVariable] = formConfig.variables[targetVariable] || '';
            }
          }
        }
      }
    }

    if (!equal(variables, updatedVariables)) {
      setVariables(updatedVariables);
    }
  }, [
    formData,
    formContext,
    formConfig.logic,
    formConfig.variables,
    variables,
    setVariables,
  ]);

  return variables;
}

type FormProps = {
  formConfig: FormConfig;
  formContext: Record<string, any>;
  responseId: string;
  metadata: Record<string, string>;
  variant?: "standalone" | "embedded";
  onSubmitted?: () => void;
};

const Form: FC<FormProps> = ({
  formConfig,
  formContext,
  responseId,
  metadata,
  variant = "standalone",
  onSubmitted,
}) => {
  // Embedded mode renders inside another page's chrome (e.g. the arrangement
  // wizard), so the form suppresses its own Header and progress bar.
  const showChrome = variant !== "embedded";
  const headerEl = showChrome ? (
    <Header properties={formConfig.properties} />
  ) : null;
  const posthog = usePostHog();
  const {
    formData: initialFormData,
    formState: persistedFormState,
    updateFormData: setStoredFormData,
    updateFormState: setStoredFormState,
    clearAll: clearStoredForm,
  } = useFormStorage(formConfig.id, responseId, formConfig);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    register,
    setValue,
    setFocus,
    watch,
    trigger,
    getValues,
    formState,
  } = methods;
  const { errors } = formState;

  // Compute the form's variables given current form field values
  const variables = useFormVariables({ formConfig, formContext, watch });
  const { scheduleRedirect } = useFormRedirect({
    formConfig,
    formContext,
    variables,
  });

  // On mount, restore the form data that was saved
  useEffect(() => {
    if (initialFormData) {
      Object.entries(initialFormData).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, []);

  // State to keep track of field history - only use persisted state if show_review is enabled
  const [fieldHistory, setFieldHistory] = useSafeState<string[]>(
    formConfig.properties?.show_review &&
      persistedFormState.fieldHistory.length > 0
      ? persistedFormState.fieldHistory
      : formConfig.start_screens?.length
        ? [] // Start empty if there are start screens
        : [formConfig.fields[0].ref], // Immediately go to first field if no start screen
  );
  const [currentEndScreenRef, setCurrentEndScreenRef] = useSafeState<
    string | null
  >(null);
  const [submissionStatus, setSubmissionStatus] = useSafeState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [submissionResponse, setSubmissionResponse] = useSafeState<any>(null);
  const [submissionError, setSubmissionError] = useSafeState<any>(null);

  // Review mode state - only use persisted state if show_review is enabled
  const [isReviewMode, setIsReviewMode] = useSafeState(
    formConfig.properties?.show_review
      ? persistedFormState.isReviewMode
      : false,
  );
  const [returnToReview, setReturnToReview] = useSafeState(
    formConfig.properties?.show_review
      ? persistedFormState.returnToReview
      : false,
  );
  const [isSavingToReview, setIsSavingToReview] = useSafeState(false);

  // Save form state to localStorage when it changes
  useEffect(() => {
    setStoredFormState({
      isReviewMode,
      returnToReview,
      fieldHistory,
    });
  }, [isReviewMode, returnToReview, fieldHistory, setStoredFormState]);

  // Get the current field ref from the history
  const currentFieldRef = fieldHistory[fieldHistory.length - 1];

  // Find the current field from the formConfig using the currentFieldRef
  const currentField = useMemo(
    () =>
      formConfig.fields.find((field) => field.ref === currentFieldRef) ||
      formConfig.fields[0],
    [formConfig.fields, currentFieldRef],
  );

  const validatedFieldsRef = useRef<Record<string, boolean>>({});

  // Trigger validation when the current field changes
  useEffect(() => {
    const fieldRef = currentField.ref;

    if (!validatedFieldsRef.current[fieldRef]) {
      validatedFieldsRef.current[fieldRef] = true;
      trigger(fieldRef);
    }
  }, [currentField, trigger]);

  // Directly access errors[currentField.ref] to subscribe to changes
  const isCurrentFieldValid = !errors[currentField.ref];

  const nextField = async () => {
    if (isCurrentFieldValid) {
      const formData = getValues();
      setStoredFormData(formData);

      // If we're returning to review, save and go back to review screen
      if (returnToReview) {
        await saveAndReturnToReview();
        return;
      }

      const logicJump = formConfig.logic?.find(
        (lj) => lj.type === "field" && lj.ref === currentField.ref,
      );

      let nextFieldRef: string | null = null;
      let nextEndScreenRef: string | null = null;

      if (logicJump) {
        for (const action of logicJump.actions) {
          const conditionMet = evaluateCondition(
            action.condition,
            getValues,
            variables,
            formContext,
          );

          if (conditionMet) {
            if (action.action === "jump") {
              if (action.details.to.type === "field") {
                nextFieldRef = action.details.to.value;
              } else if (action.details.to.type === "end_screen") {
                nextEndScreenRef = action.details.to.value;
              }
              break; // Stop processing actions after the first 'jump'
            }
          }
        }
      }

      if (nextEndScreenRef) {
        // Check if we should show review screen before submitting
        if (formConfig.properties?.show_review && !returnToReview) {
          await saveAndReturnToReview();
        } else {
          // Submit the form
          try {
            await submitFormData(formData, nextEndScreenRef);
          } catch (error) {
            Sentry.captureException(error);
            // TODO handle error
          }
        }
      } else if (nextFieldRef) {
        // Non-critical save - don't block navigation
        saveFormProgress(formData, currentField.ref).catch((error) => {
          console.error("Error saving form progress:", error);
          Sentry.captureException(error);
        });

        // If nextFieldRef is found, add it to the fieldHistory
        setFieldHistory((prevHistory) => [...prevHistory, nextFieldRef!]);
      } else {
        // Proceed to the next field in the formConfig.fields array
        const currentIndex = formConfig.fields.findIndex(
          (field) => field.ref === currentField.ref,
        );

        if (currentIndex < formConfig.fields.length - 1) {
          // Non-critical save - don't block navigation
          saveFormProgress(formData, currentField.ref).catch((error) => {
            console.error("Error saving form progress:", error);
            Sentry.captureException(error);
          });

          const nextField = formConfig.fields[currentIndex + 1];
          setFieldHistory((prevHistory) => [...prevHistory, nextField.ref]);
        } else {
          // No more fields
          if (formConfig.properties?.show_review || returnToReview) {
            await saveAndReturnToReview();
          } else {
            // Submit the form
            try {
              await submitFormData(formData, "default");
            } catch (error) {
              Sentry.captureException(error);
              // TODO handle error
            }
          }
        }
      }
    } else {
      // Optionally handle invalid field
      Sentry.captureMessage("Invalid situation in Form:nextField");
    }
  };

  const submitFormData = async (
    data: Record<string, any>,
    endScreenRef: string,
  ) => {
    setSubmissionStatus("submitting");
    setStoredFormData(data);

    Sentry.addBreadcrumb({
      category: "forms.submit",
      message: "Starting form submission",
      level: "info",
      data: {
        form_id: formConfig.id,
        response_id: responseId,
        end_screen_ref: endScreenRef,
        has_review: formConfig.properties?.show_review,
      },
    });

    posthog?.capture("forms_submit", {
      form_id: formConfig.id,
      response_id: responseId,
      ...flatten("metadata", metadata),
    });

    // Get relevant field refs based on the final answers
    const relevantFieldRefs = getRelevantFieldRefs(
      formConfig,
      formContext,
      data,
    );

    // Filter data to include only relevant fields and their subfields
    const filteredData = Object.keys(data)
      .filter((key) => relevantFieldRefs.some((ref: string) => key === ref))
      .reduce(
        (obj, key) => {
          obj[key] = data[key];
          return obj;
        },
        {} as Record<string, any>,
      );

    // Build the final form response data
    const formResponseData = buildFormResponseData(filteredData, formConfig);

    // Submit the final data to the API
    try {
      const response = await fetchPublicAPI(
        `/forms/${formConfig.id}/${responseId}/complete-submission`,
        {
          method: "POST",
          body: JSON.stringify({ ...formResponseData, variables }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      setSubmissionResponse(response);
      setSubmissionStatus("success");

      Sentry.addBreadcrumb({
        category: "forms.submit",
        message: "Form submission successful",
        level: "info",
        data: {
          form_id: formConfig.id,
          response_id: responseId,
          end_screen_ref: endScreenRef,
          has_end_screen_auto_redirect: Boolean(
            formConfig.end_screens.find((screen) => screen.ref === endScreenRef)
              ?.properties?.auto_redirect,
          ),
        },
      });

      // CRITICAL: Set end screen BEFORE clearing field history to prevent blank screen
      // This order is essential - DO NOT REORDER these state updates
      setCurrentEndScreenRef(endScreenRef);
      setIsReviewMode(false);
      setReturnToReview(false);
      setFieldHistory([]);
      clearStoredForm();
      onSubmitted?.();
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionError(error);
      setSubmissionStatus("error");

      Sentry.captureException(error, {
        tags: {
          form_id: formConfig.id,
          response_id: responseId,
        },
        contexts: {
          form_submission: {
            end_screen_ref: endScreenRef,
            field_count: Object.keys(data).length,
          },
        },
      });

      // CRITICAL: Still show end screen even on error to prevent blank screen
      setCurrentEndScreenRef(endScreenRef);
    }
  };

  const prevField = () => {
    if (fieldHistory.length > 1) {
      // Remove the current field
      setFieldHistory((prevHistory) => prevHistory.slice(0, -1));
    }
  };

  const saveFormProgress = async (
    formData: Record<string, any>,
    fieldRef: string,
  ) => {
    const answer = buildAnswer(formData, fieldRef, formConfig);

    if (!answer) {
      return;
    }

    const formResponseData = {
      answer, // Sending a single answer instead of an array
    };

    Sentry.addBreadcrumb({
      category: "forms.save_progress",
      message: "Save form progress",
      level: "info",
      data: {
        form_id: formConfig.id,
        response_id: responseId,
        field_ref: fieldRef,
      },
    });

    posthog?.capture("forms_save_progress", {
      form_id: formConfig.id,
      response_id: responseId,
      field_ref: fieldRef,
      ...flatten("metadata", metadata),
    });

    // Send progress update to API - now awaited for critical paths
    await fetchPublicAPI(`/forms/${formConfig.id}/${responseId}`, {
      method: "PATCH",
      body: JSON.stringify(formResponseData),
      headers: {
        "Content-Type": "application/json",
      },
    });
  };

  const onSubmitForm = async () => {
    await nextField();
  };

  // Helper function to save progress and return to review screen
  const saveAndReturnToReview = async () => {
    setIsSavingToReview(true);
    try {
      const formData = getValues();
      setStoredFormData(formData);

      // Save progress before showing review screen
      await saveFormProgress(formData, currentField.ref);

      // Get all relevant fields and trigger validation before showing review
      const relevantFieldRefs = getRelevantFieldRefs(
        formConfig,
        formContext,
        formData,
      );

      await trigger(relevantFieldRefs);

      // Show review screen
      setIsReviewMode(true);
      setReturnToReview(false);
    } catch (error) {
      console.error("Error saving form progress before review:", error);
      Sentry.captureException(error);
      // Still allow returning to review even if save fails
      setIsReviewMode(true);
      setReturnToReview(false);
    } finally {
      setIsSavingToReview(false);
    }
  };

  // =======================

  const formData = watch();

  const isNextStepEndScreen = useMemo(() => {
    // If review mode is enabled, never show submit text on the last field
    if (formConfig.properties?.show_review) {
      return false;
    }

    const logicJump = formConfig.logic?.find(
      (lj) => lj.type === "field" && lj.ref === currentField.ref,
    );

    let nextIsEndScreen = false;

    if (logicJump) {
      for (const action of logicJump.actions) {
        const conditionMet = evaluateCondition(
          action.condition,
          () => formData,
          variables,
          formContext,
        );

        if (conditionMet) {
          if (action.action === "jump") {
            if (action.details.to.type === "end_screen") {
              nextIsEndScreen = true;
            }
            break; // Stop processing actions after the first 'jump'
          }
        }
      }
    } else {
      const currentIndex = formConfig.fields.findIndex(
        (field) => field.ref === currentField.ref,
      );
      if (currentIndex >= formConfig.fields.length - 1) {
        // We are on the last field
        nextIsEndScreen = true;
      }
    }

    return nextIsEndScreen;
  }, [
    formConfig.logic,
    formConfig.fields,
    currentField.ref,
    variables,
    formContext,
    formData,
  ]);

  // =======================

  // Add new state for start screen - skip if user has previous progress (only if show_review is enabled)
  const [showStartScreen, setShowStartScreen] = useState(() => {
    if (!formConfig.start_screens?.length) return false;

    // Only check for previous progress if show_review is enabled
    if (formConfig.properties?.show_review) {
      // Skip start screen if user has previous progress
      if (persistedFormState.isReviewMode || persistedFormState.returnToReview)
        return false;
      if (persistedFormState.fieldHistory.length > 0) return false;
    }

    return true;
  });

  // Handle focus - runs when field changes OR when start screen is dismissed
  useEffect(() => {
    // Don't focus if we're still showing the start screen
    if (showStartScreen) return;

    const autofocusElement = document.querySelector('[data-autofocus="true"]');

    if (autofocusElement instanceof HTMLElement) {
      autofocusElement.focus();
    } else {
      // Fallback: try react-hook-form's setFocus for simple fields
      setFocus(currentField.ref);
    }
  }, [currentField, setFocus, showStartScreen]);

  const startScreen = useMemo(() => {
    if (!formConfig.start_screens?.length) return null;

    // Check for logic jumps that define start screens
    const startScreenLogic = formConfig.logic?.find(
      (lj) => lj.type === "start",
    );

    if (startScreenLogic) {
      for (const action of startScreenLogic.actions) {
        if (
          action.action === "jump" &&
          action.details.to.type === "start_screen"
        ) {
          const conditionMet = evaluateCondition(
            action.condition,
            () => ({}), // No form data yet for start screen
            variables,
            { ...formConfig.context, ...formContext }, // Merge both contexts
          );

          if (conditionMet) {
            return formConfig.start_screens.find(
              (screen) => screen.ref === action.details.to.value,
            );
          }
        }
      }
    }

    // If no logic matches or no logic exists, fall back to default
    return formConfig.start_screens.find((screen) => screen.ref === "default");
  }, [formConfig.start_screens, formConfig.logic, variables, formContext]);

  const progress = useFormProgress({
    formConfig,
    currentFieldRef: currentField?.ref || null,
    currentEndScreenRef,
  });

  const currentEndScreen = useMemo(() => {
    if (!currentEndScreenRef) return null;

    return (
      formConfig.end_screens.find(
        (screen) => screen.ref === currentEndScreenRef,
      ) || {
        title: "Thank you!",
        ref: "default",
        properties: {
          description: "Your submission has been recorded",
        },
      }
    );
  }, [currentEndScreenRef, formConfig.end_screens]);

  useEffect(() => {
    if (!currentEndScreen || submissionStatus !== "success") return undefined;
    return scheduleRedirect(currentEndScreen.properties?.auto_redirect);
  }, [currentEndScreen, scheduleRedirect, submissionStatus]);

  // Handle editing a field from the review screen
  const editFieldFromReview = (fieldRef: string) => {
    // Find the field index in the history
    const fieldIndex = fieldHistory.findIndex((ref) => ref === fieldRef);

    if (fieldIndex !== -1) {
      // Jump to that point in history
      setFieldHistory(fieldHistory.slice(0, fieldIndex + 1));
    } else {
      // Field wasn't in history (shouldn't happen), add it
      setFieldHistory((prevHistory) => [...prevHistory, fieldRef]);
    }

    setIsReviewMode(false);
    setReturnToReview(true);
  };

  // Handle final submission from review screen
  const submitFromReview = async () => {
    try {
      const formData = getValues();
      // Recompute the end screen from the latest answers so edits made on the
      // review screen still route to the correct end screen.
      const endScreenRef = resolveEndScreenRef(
        formConfig,
        formContext,
        formData,
      );
      await submitFormData(formData, endScreenRef);
    } catch (error) {
      Sentry.captureException(error);
      // TODO handle error
    }
  };

  // Early return for start screen, but only if we found one
  if (showStartScreen && startScreen) {
    return (
      <FormConfigProvider
        config={formConfig}
        variables={variables}
        context={formContext}
        responseId={responseId}
      >
        {headerEl}
        <div className="mx-auto my-6 max-w-xl px-3 sm:my-16 sm:px-4">
          <Screen
            screen={startScreen}
            isStartScreen={true}
            onStart={() => {
              setShowStartScreen(false);
              // Initialize field history when user actually starts the form
              setFieldHistory([formConfig.fields[0].ref]);
            }}
          />
        </div>
      </FormConfigProvider>
    );
  }

  // Early return for end screen (updated to use Screen component)
  // This MUST come before the fieldHistory check, otherwise clearing fieldHistory on submit causes blank screen
  if (currentEndScreen) {
    return (
      <FormConfigProvider
        config={formConfig}
        variables={variables}
        context={formContext}
        responseId={responseId}
      >
        {headerEl}
        <div className="mx-auto my-6 max-w-xl px-3 sm:my-16 sm:px-4">
          <Screen
            screen={currentEndScreen}
            response={submissionResponse}
            error={submissionError}
            isStartScreen={false}
          />
        </div>
      </FormConfigProvider>
    );
  }

  // If we have start screens but no field history, user hasn't started yet
  // Don't render form fields until they click "Start"
  if (formConfig.start_screens?.length && fieldHistory.length === 0) {
    // CRITICAL: This should never happen after submission - log to Sentry
    if (submissionStatus === "success" || submissionStatus === "submitting") {
      Sentry.captureMessage(
        "CRITICAL: Blank screen detected after form submission",
        {
          level: "error",
          tags: {
            form_id: formConfig.id,
            response_id: responseId,
            submission_status: submissionStatus,
          },
          contexts: {
            form_state: {
              has_start_screens: !!formConfig.start_screens?.length,
              field_history_length: fieldHistory.length,
              current_end_screen_ref: currentEndScreenRef,
              show_start_screen: showStartScreen,
              is_review_mode: isReviewMode,
            },
          },
        },
      );

      // EMERGENCY FALLBACK: Show a default end screen instead of blank
      return (
        <FormConfigProvider
          config={formConfig}
          variables={variables}
          context={formContext}
          responseId={responseId}
        >
          {headerEl}
          <div className="mx-auto my-6 max-w-xl px-3 sm:my-16 sm:px-4">
            <Screen
              screen={{
                ref: "emergency_fallback",
                title: "Thank you for your submission",
                properties: {
                  description:
                    "Your information has been received and our team will be in touch shortly.",
                },
              }}
              response={submissionResponse}
              error={submissionError}
              isStartScreen={false}
            />
          </div>
        </FormConfigProvider>
      );
    }
    return null; // This should only happen on initial load with start screens
  }

  if (isReviewMode) {
    return (
      <FormConfigProvider
        config={formConfig}
        variables={variables}
        context={formContext}
        responseId={responseId}
      >
        <FormProvider {...methods}>
          {headerEl}
          <div className="mx-auto my-6 max-w-xl px-3 sm:my-16 sm:px-4">
            <ReviewScreen
              formConfig={formConfig}
              onEditField={editFieldFromReview}
              onSubmit={submitFromReview}
              submissionStatus={submissionStatus}
            />
          </div>
        </FormProvider>
      </FormConfigProvider>
    );
  }

  // Add this new function to handle field selection from debug panel
  const jumpToField = (fieldRef: string) => {
    setFieldHistory((prevHistory) => [...prevHistory, fieldRef]);
  };

  // Handle jumping to start screen from debug panel
  const jumpToStartScreen = (_screenRef: string) => {
    // Reset state to show start screen
    // Note: The start screen shown is determined by logic/context, not by ref
    setShowStartScreen(true);
    setFieldHistory([]);
    setCurrentEndScreenRef(null);
    setIsReviewMode(false);
    setReturnToReview(false);
  };

  // Handle jumping to end screen from debug panel
  const jumpToEndScreen = (screenRef: string) => {
    // Set the end screen without submitting
    setCurrentEndScreenRef(screenRef);
    setIsReviewMode(false);
    setReturnToReview(false);
  };

  return (
    <FormConfigProvider
      config={formConfig}
      variables={variables}
      context={formContext}
      responseId={responseId}
    >
      <FormProvider {...methods}>
        <div className="relative">
          {showChrome && formConfig.properties?.show_progress && (
            <ProgressBar progress={progress} />
          )}
          {headerEl}

          <div className="mx-auto my-6 max-w-xl px-3 sm:my-16 sm:px-4">
            <AriaForm
              onSubmit={handleSubmit(onSubmitForm)}
              className="space-y-6"
            >
              <Field
                key={currentField.ref}
                field={currentField}
                control={control}
                register={register}
                setValue={setValue}
              />

              <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse sm:items-center sm:justify-end sm:gap-0">
                {/* Review button - shown below on mobile, inline on desktop */}
                {returnToReview && (
                  <div className={`order-2 w-full sm:order-none sm:grow`}>
                    <Button
                      onPress={async () => {
                        if (isCurrentFieldValid) {
                          await saveAndReturnToReview();
                        }
                      }}
                      className={`flex w-full items-center justify-center whitespace-nowrap rounded bg-olive px-6 py-2 font-bold text-soil ring-offset-4 transition-all hover:bg-soil hover:text-olive hover:outline-soil focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope disabled:pointer-events-none disabled:opacity-50`}
                      isDisabled={
                        !isCurrentFieldValid ||
                        submissionStatus === "submitting" ||
                        isSavingToReview
                      }
                      aria-label="Save and return to review"
                    >
                      {isSavingToReview ? (
                        <>
                          <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save & Return to Review"
                      )}
                    </Button>
                  </div>
                )}

                {/* Back and Continue buttons container */}
                <div className="order-1 flex grow flex-row-reverse items-center justify-end sm:order-none">
                  <div className={`flex grow flex-row-reverse items-center`}>
                    {fieldHistory.length > 0 && !returnToReview && (
                      <Button
                        onPress={nextField}
                        className={`flex grow items-center justify-center whitespace-nowrap rounded bg-cantelope px-4 py-2 font-bold text-soil ring-offset-4 transition-all duration-500 hover:bg-soil hover:text-cantelope hover:outline-soil focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope disabled:pointer-events-none disabled:opacity-50 sm:px-6`}
                        isDisabled={
                          !isCurrentFieldValid ||
                          submissionStatus === "submitting" ||
                          isSavingToReview
                        }
                        aria-label={
                          isSavingToReview
                            ? "Saving progress"
                            : isNextStepEndScreen
                              ? "Submit form"
                              : "Continue to next field"
                        }
                      >
                        {isSavingToReview ? (
                          <>
                            <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                            Saving...
                          </>
                        ) : isNextStepEndScreen ? (
                          submissionStatus === "submitting" ? (
                            <>
                              <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                              Submitting...
                            </>
                          ) : formConfig.properties?.show_review ? (
                            "Next"
                          ) : (
                            "Submit"
                          )
                        ) : (
                          "Next"
                        )}
                      </Button>
                    )}
                  </div>

                  <div
                    className={`overflow-hidden transition-all duration-500 ease-out ${
                      fieldHistory.length > 1 &&
                      !returnToReview &&
                      !isSavingToReview
                        ? "mr-2 max-w-[60px] opacity-100 sm:mr-4"
                        : "max-w-0 opacity-0"
                    }`}
                  >
                    <Button
                      onPress={prevField}
                      className={`whitespace-nowrap rounded bg-oat px-2 py-2.5 text-soil transition-all hover:bg-oat-300 focus:bg-oat-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope ${
                        fieldHistory.length > 1 &&
                        !returnToReview &&
                        !isSavingToReview
                          ? ""
                          : "pointer-events-none"
                      }`}
                      isDisabled={
                        fieldHistory.length <= 1 ||
                        returnToReview ||
                        isSavingToReview
                      }
                      aria-label="Go to previous field"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </AriaForm>
          </div>

          <DebugPanel
            fields={formConfig.fields}
            currentFieldRef={currentField.ref}
            onFieldSelect={jumpToField}
            startScreens={formConfig.start_screens}
            endScreens={formConfig.end_screens}
            onStartScreenSelect={jumpToStartScreen}
            onEndScreenSelect={jumpToEndScreen}
            currentEndScreenRef={currentEndScreenRef}
          />
        </div>
      </FormProvider>
    </FormConfigProvider>
  );
};

export default Form;
